import React, { Fragment, useEffect, useState } from "react";
import { SidePopup } from "../../components/model-popup/side-popup";
import { CustomButton } from "../../components/buttons/button";
import { useFormik } from "formik";
import * as yup from "yup";
import TextIconInput from "../../components/custom-fields/input";
import ErrorMessage from "../../components/custom-fields/error-message";
import { alphabetValidate } from "../../utils/field-validation";
import { createRoleAndAccess } from "../../api/create";
import { RolePutAPI } from "../../api/update";
import { toast } from "react-toastify";
import CheckboxField from "../../components/custom-fields/checkbox";
import { ToggleButton } from "../../components/input-fields/toggle-button";


export const RolesPopup = (props: any) => {
    const {
        modules,
        showModal,
        setShowModal,
        title,
        handleClose,
        poupDetails,
        setPoupDetails,
        afterSubmitModalForm
    } = props;

    const [buttonLoader, setButtonLoader] = useState<boolean>(false);
    const [, setOpen] = useState<any>(null);
    const [, setShowPassword] = useState<boolean>(false);
    const [viewOnly, setViewOnly] = useState<any>(false);

    const moduleAccessSchema = yup.object({
        moduleId: yup.number().required(),
        read: yup.bool().required(),
        write: yup.bool().required(),
        update: yup.bool().required(),
        delete: yup.bool().required(),
    });

    const handleCheckModuleAccess = (moduleName: string, permission: string): boolean => {
        const moduleDetails: Record<string, string[]> = {
            dashboard: ['read'],
            users: ['read', 'write', 'update', 'delete'],
            'patient lookup': ['read'],
            'mapping': ['read', 'write', 'update', 'delete'],
            'role and access': ['read', 'write', 'update', 'delete'],
            'all notes': ['read'],
            'error queues': ['read', 'update', 'delete'],
            'deleted notes': ['read', 'update'],
            'ai': ['read', 'write', 'update', 'delete'],
            'auto delete': ['read', 'write', 'delete'],
        };

        const commonPermissions = ['read', 'update', 'delete'];
        const moduleNameLowerCase = moduleName.toLowerCase();
        const selectedPermissions = moduleDetails[moduleNameLowerCase] ?? commonPermissions;

        return selectedPermissions.includes(permission)
    };


    const handleCheckboxVisibility = (moduleName: string, permission: string): string => {
        return handleCheckModuleAccess(moduleName, permission) ? ' visible' : ' invisible';
    };


    // Check one access validation
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const roleAccessSchema = yup
        .array(moduleAccessSchema)
        .test(
            "at-least-one-permission",
            "At least one access must be selected",
            //   eslint-disable-next-line
            (value: any) =>
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                value.some(
                    (module: any) =>
                        // eslint-disable-next-line
                        module.read || module.write || module.update || module.delete
                )
        );

    const formik: any = useFormik({
        enableReinitialize: true,
        initialValues: {
            role_name: poupDetails?.role_name || "",
            roleAccess: modules?.map((item: any) => {
                const accessItem = poupDetails?.access_list?.find((access: any) =>
                    access?.module_id === item?.id);
                return {
                    moduleId: item?.id,
                    moduleName: item?.module_name,
                    read: item?.module_name === 'Patient Lookup' ? true : accessItem?.read_access ?? false,  // Default to false if not found
                    write: handleCheckModuleAccess(item?.module_name, 'write') ? accessItem?.write_access ?? false : false, // Default to false if not found
                    update: handleCheckModuleAccess(item?.module_name, 'update') ? accessItem?.update_access ?? false : false, // Default to false if not found
                    delete: handleCheckModuleAccess(item?.module_name, 'delete') ? accessItem?.delete_access ?? false : false, // Default to false if not found
                };
            }),
        },
        validationSchema: yup.object({
            role_name: yup.string().required("Role Name is required"),
            roleAccess: roleAccessSchema,
        }),
        onSubmit: async (values: any) => {
            setButtonLoader(true);
            const data = {
                ...values,

            }
            data.access = data.roleAccess.map((item: any) => ({
                module_id: item?.moduleId,
                read_access: item?.read,
                write_access: item?.write,
                update_access: item?.update,
                delete_access: item?.delete,
            }))
            delete data.roleAccess
            const handleUploadAPI = async (method: string) => {
                if (method === "put") {
                    await RolePutAPI(poupDetails?.id, {
                        ...data
                    })
                        .then(async (res: any) => {
                            if (res?.success) {
                                toast.success(res?.message || "Role updated success");
                                afterSubmitModalForm();
                                setShowModal(false);
                                formik?.resetForm();
                            } else {
                                toast.error(res?.detail?.message || "Role updated failed");
                            }
                        })
                        .catch((err: any) => {
                            console.error(err);
                        });
                } else if (method === "post") {
                    await createRoleAndAccess({
                        ...data,
                    })
                        .then(async (res: any) => {
                            if (res?.success) {
                                toast.success(res?.message || "Role created success");
                                afterSubmitModalForm();
                                setShowModal(false);
                                formik?.resetForm();
                            } else {
                                toast.error(res?.detail?.message || "Role creation failed");
                            }
                        })
                        .catch((err: any) => {
                            console.error(err);
                        });
                }
                setButtonLoader(false);
            };

            await handleUploadAPI(title === "Add New Role" ? "post" : "put");
        },
    });
    const handleToggleViewOnlyDisable = () => {
        // eslint-disable-next-line
        let isChecked;
        // eslint-disable-next-line
        isChecked = formik?.values?.roleAccess?.every(
            (item: any) =>
                item?.read && !item?.write && !item?.update && !item?.delete
        );
        setViewOnly(isChecked);
    };

    useEffect(() => {
        handleToggleViewOnlyDisable();
        poupDetails?.access?.map((item: any) => ({
            moduleId: item?.module_id,
            read: item?.module_name === 'Patient Lookup' ? true : item?.read_access,
            write: item?.write_access,
            update: item?.update_access,
            delete: item?.delete_access,
            moduleName: item?.module_name,
        }))
    }, [formik?.values?.roleAccess]);

    const handleCheckbox = (e: any, index: number) => {
        const { checked, name } = e.target;
        checked && formik.setFieldValue(`roleAccess.${index}.read`, true);
        formik.setFieldValue(name, checked);
    };

    const handleToggleViewOnly = (e: any) => {
        const isChecked = e.target.checked;
        setViewOnly(e.target.checked);

        const accessData = poupDetails?.access?.length
            ? poupDetails.access?.map((item: any) => ({
                moduleId: item?.module_id,
                read: item?.module_name === 'Patient Lookup' ? true : isChecked,
                write: false,
                update: false,
                delete: false,
                moduleName: item.module_name,
            }))
            : modules?.map((item: any) => ({
                moduleId: item?.id,
                read: item?.module_name === 'Patient Lookup' ? true : isChecked,
                write: false,
                update: false,
                delete: false,
                moduleName: item.module_name,
            }));
        formik.setFieldValue("roleAccess", accessData);
    };

    async function handleErrorFields() {
        await formik.validateForm();
        if (formik?.errors) {
            const errorField: any = Object.keys(formik.errors)?.[0];
            document.getElementById(errorField)?.focus();
            document.getElementById(errorField)?.scrollIntoView({
                block: "center",
                inline: "end",
                behavior: "smooth",
            });
        }
    }



    useEffect(() => {
        console.log(formik?.values?.roleAccess, 'formik?.values?.roleAccess')
    }, [formik?.values?.roleAccess]);

    return (
        //eslint-disable-next-line
        //@ts-ignore
        <SidePopup
            showModal={showModal}
            handleClose={handleClose}
            title={title}
            clearFormik={() => formik.resetForm()}
            closeIcon={false}
            footerStyle="flex justify-end gap-[16px] "
            footerContent={
                <>
                    <CustomButton
                        buttonStyle="text-[#031227] bg-white hover:bg-[#F5F5F5] disabled:bg-white disabled:text-[#AAADB1]  px-[26px] py-[12px] text-[14px] font-semibold leading-[17px] border-[#E8ECF4] "
                        buttonName="Cancel"
                        primary={false}
                        disable={buttonLoader}
                        onClick={() => {
                            setShowModal(false);
                            setPoupDetails({});
                            setViewOnly(false);
                            setShowPassword(false);
                            formik.resetForm();
                        }}
                    />
                    <CustomButton
                        type="submit"
                        loader={buttonLoader}
                        loaderStyle={` px-[40.5px] py-[13px] leading-[17px]`}
                        primary={true}
                        disable={buttonLoader}
                        buttonStyle="text-white px-[36px] py-[12px] text-[14px] font-semibold leading-[17px] hover:bg-[#166B5E] disabled:bg-[#75C8BB]  "
                        buttonName={title === "Add New Role" ? "Add" : "Update"}
                        onClick={() => {
                            handleErrorFields();
                            formik.handleSubmit();
                        }}
                    />
                </>
            }
        >


            <div
                className="pt-[8px] grid grid-cols-1 gap-x-[12px]"
                onClick={() => {
                    setOpen(null);
                }}
            >
                <div className="w-full col-span-1 mb-[5px]">
                    <TextIconInput
                        label="Role Name"
                        name="role_name"
                        id="role_name"
                        type="text"
                        disable={buttonLoader}
                        maxLength={30}
                        value={formik.values.role_name}
                        onChange={formik.handleChange}
                        onkeypress={alphabetValidate}
                        errors={formik.errors.name && formik.touched.name}
                        placeholder="Enter name"
                        onBlur={formik.handleBlur}
                        required
                    />
                    {formik.errors.role_name && formik.touched.role_name ? (
                        <ErrorMessage>{formik.errors.role_name}</ErrorMessage>
                    ) : (
                        <span>&nbsp;</span>
                    )}
                </div>
                {/* Modules Role and Access Section   */}
                <div id="roleAccess">
                    <h3 className="text-[16px] text-[#1E1E1E] font-medium mb-[16px]">
                        Access
                    </h3>
                    <div className="w-full flex items-center gap-[8px] justify-between pb-1">
                        <div>
                            <h3 className="text-[14px] pb-1 text-[#1E1E1E] font-medium">
                                View Only
                            </h3>
                            <div className="text-[12px] text-[#9AA0A9] font-normal">
                                Use this to give View only access to all modules
                            </div>
                        </div>
                        <ToggleButton
                            onChange={(e: any) => {
                                handleToggleViewOnly(e);
                            }}
                            value={viewOnly}
                            // onBlur
                            disabled={buttonLoader}
                            id="viewToggle"
                            name="viewToggle"
                            toggleStyle="w-[45.5px] h-[26px] after:h-[21px] after:w-[21px] after:top-[2.2px]"
                        />
                    </div>
                    {formik.errors.roleAccess ? (
                        <ErrorMessage>{formik.errors.roleAccess}</ErrorMessage>
                    ) : (
                        <span>&nbsp;</span>
                    )}
                    <div className="w-full grid grid-cols-9 items-center gap-[8px]  justify-between pt-[16px] pb-[12px]  border-y my-[16px]">
                        <div className="col-span-5 text-[14px] font-medium text-[#1E1E1E]">
                            Modules
                        </div>
                        <div className="col-span-1 text-[14px] font-medium text-[#1E1E1E] text-center">
                            View
                        </div>
                        <div className="col-span-1 text-[14px] font-medium text-[#1E1E1E] text-center">
                            Add
                        </div>
                        <div className="col-span-1 text-[14px] font-medium text-[#1E1E1E] text-center">
                            Edit
                        </div>
                        <div className="col-span-1 text-[14px] font-medium text-[#1E1E1E] text-center">
                            Delete
                        </div>
                    </div>
                    <div className="w-full grid grid-cols-9 items-center gap-[8px]  justify-between ">
                        {formik?.values?.roleAccess?.length > 0 &&
                            formik?.values?.roleAccess?.map(
                                (value: any, index: number) =>
                                    <Fragment key={index}>
                                        <div className="col-span-5 text-[14px] font-medium text-[#5C5E61]">
                                            {value?.moduleName}
                                        </div>
                                        <div className="col-span-1 inline-flex items-center justify-center">
                                            <CheckboxField
                                                disabled={value?.moduleName === 'Patient Lookup' ? true :
                                                    buttonLoader ||
                                                    value?.write ||
                                                    value?.update ||
                                                    value?.delete
                                                }
                                                value={value?.read}
                                                name={`roleAccess.${index}.read`}
                                                id={`roleAccess.${index}.read`}
                                                checkboxStyle={handleCheckboxVisibility(value?.moduleName, 'read')}
                                                checked={value?.read}
                                                onChange={formik.handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-span-1 inline-flex items-center justify-center">
                                            <CheckboxField
                                                disabled={buttonLoader}
                                                name={`roleAccess.${index}.write`}
                                                id={`roleAccess.${index}.write`}
                                                value={value?.write}
                                                checked={value?.write}
                                                checkboxStyle={handleCheckboxVisibility(value?.moduleName, 'write')}
                                                onChange={(e: any) => {
                                                    e.target.checked &&
                                                        formik.setFieldValue(
                                                            `roleAccess.${index}.read`,
                                                            true
                                                        );
                                                    formik.handleChange(e);
                                                }}
                                                required
                                            />
                                        </div>
                                        <div className="col-span-1 inline-flex items-center justify-center">
                                            <CheckboxField
                                                disabled={buttonLoader}
                                                name={`roleAccess.${index}.update`}
                                                id={`roleAccess.${index}.update`}
                                                value={value?.update}
                                                checked={value?.update}
                                                checkboxStyle={handleCheckboxVisibility(value?.moduleName, 'update')}
                                                onChange={(e: any) => {
                                                    handleCheckbox(e, index);
                                                }}
                                                required
                                            />
                                        </div>
                                        <div className="col-span-1 inline-flex items-center justify-center">
                                            <CheckboxField
                                                disabled={buttonLoader}
                                                name={`roleAccess.${index}.delete`}
                                                id={`roleAccess.${index}.delete`}
                                                value={value?.delete}
                                                checked={value?.delete}
                                                checkboxStyle={handleCheckboxVisibility(value?.moduleName, 'delete')}
                                                onChange={(e: any) => {
                                                    handleCheckbox(e, index);
                                                }}
                                                required
                                            />
                                        </div>

                                    </Fragment>

                            )}
                    </div>
                </div>
            </div>
        </SidePopup>
    );
};
